/**
 * 头部组件
 * 显示应用标题、导航和用户信息
 * 优化内容：导航动画、用户角色标识、下拉菜单动画
 */
import { useState } from 'react';

// SVG 图标组件
const SparklesIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
  </svg>
);

const MessageSquareIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const BookOpenIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
  </svg>
);

const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const LogoutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const CrownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 11a4 4 0 0 1 0 8H8a4 4 0 0 1 0-8"/>
    <path d="M12 14V3"/>
  </svg>
);

const Header = ({ activeTab, setActiveTab, user, onLogout }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);

  const isAdmin = user?.role === 'admin';

  return (
    <header className="glass-card mx-4 mt-4 glow-blue bg-[#0f0f23]/95">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="ai-avatar w-12 h-12 floating-icon">
              <SparklesIcon />
            </div>
            <div>
              <h1 className="text-2xl font-bold gradient-text">巨猿人知识库问答系统</h1>
              <p className="text-gray-400 text-sm">AI 驱动的智能问答助手</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <nav className="flex space-x-3">
              <button
                onClick={() => setActiveTab('qa')}
                className={`relative flex items-center space-x-2 px-6 py-2.5 rounded-xl transition-all duration-200 gpu-accelerate will-change-all ${
                  activeTab === 'qa'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold glow-blue'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10 hover:scale-[1.02]'
                }`}
              >
                <MessageSquareIcon />
                <span>问答中心</span>
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`relative flex items-center space-x-2 px-6 py-2.5 rounded-xl transition-all duration-200 gpu-accelerate will-change-all ${
                  activeTab === 'documents'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold glow-purple'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10 hover:scale-[1.02]'
                }`}
              >
                <BookOpenIcon />
                <span>文档管理</span>
              </button>
            </nav>
            
            {/* 用户菜单 */}
            {user && (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 px-4 py-2 bg-white/5 rounded-xl hover:bg-white/10 border border-white/10 transition-all hover:scale-105"
                >
                  <div className="relative w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <UserIcon />
                    {/* 管理员标识 */}
                    {isAdmin && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center border-2 border-gray-900">
                        <CrownIcon />
                      </div>
                    )}
                  </div>
                  <div className="text-left">
                    <span className="text-gray-300 text-sm">{user.username}</span>
                    {isAdmin && (
                      <span className="block text-xs text-yellow-400">管理员</span>
                    )}
                  </div>
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 glass-card rounded-xl overflow-hidden shadow-lg z-50 animate-slide-down">
                    {/* 用户信息 */}
                    <div className="px-4 py-3 border-b border-white/10">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                          <UserIcon />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-200">{user.username}</p>
                          <p className="text-xs text-gray-500">
                            {isAdmin ? (
                              <span className="text-yellow-400 flex items-center">
                                <CrownIcon className="inline w-3 h-3 mr-1" />
                                管理员
                              </span>
                            ) : (
                              '普通用户'
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* 菜单选项 */}
                    <div className="py-2">
                      <button
                        onClick={() => {
                          onLogout();
                          setShowUserMenu(false);
                        }}
                        className="w-full px-4 py-3 flex items-center space-x-2 text-red-400 hover:bg-white/10 transition-colors hover:scale-[1.02]"
                      >
                        <LogoutIcon />
                        <span>退出登录</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;